/* eslint-disable no-restricted-syntax */
// ----------------------------------------------------------------------
// The only module allowed to touch Web Storage directly.
// Browsers can throw on any storage access: SecurityError when site data is blocked
// (e.g. Safari "Block All Cookies", some in-app browsers), QuotaExceededError on writes.
// Every helper here swallows those errors, so callers treat storage as best-effort.
// ----------------------------------------------------------------------

export type StorageKind = 'local' | 'session';

const availability: Partial<Record<StorageKind, boolean>> = {};

function getStore(kind: StorageKind): Storage {
  return kind === 'local' ? window.localStorage : window.sessionStorage;
}

export function storageAvailable(kind: StorageKind): boolean {
  const known = availability[kind];
  if (known !== undefined) {
    return known;
  }
  try {
    const store = getStore(kind);
    const key = '__storage_probe__';
    store.setItem(key, key);
    store.removeItem(key);
    availability[kind] = true;
  } catch {
    availability[kind] = false;
  }
  return availability[kind] as boolean;
}

export function safeGetItem(kind: StorageKind, key: string): string | null {
  if (!storageAvailable(kind)) {
    return null;
  }
  try {
    return getStore(kind).getItem(key);
  } catch {
    return null;
  }
}

/** @returns false when the value could not be stored. */
export function safeSetItem(kind: StorageKind, key: string, value: string): boolean {
  if (!storageAvailable(kind)) {
    return false;
  }
  try {
    getStore(kind).setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function safeRemoveItem(kind: StorageKind, key: string): void {
  if (!storageAvailable(kind)) {
    return;
  }
  try {
    getStore(kind).removeItem(key);
  } catch {
    // nothing to remove
  }
}

export function isLocalStorageEvent(event: StorageEvent): boolean {
  try {
    return event.storageArea === window.localStorage;
  } catch {
    return false;
  }
}
